import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { NavigationHighlight } from './NavigationHighlight';
import { clientOnboardingSteps, vendorOnboardingSteps } from './onboardingSteps';
import { useAuth } from '@/lib/auth';

interface OnboardingTourModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  tourKey?: number;
}

export function OnboardingTourModal({ 
  open, 
  onOpenChange, 
  onComplete,
  tourKey
}: OnboardingTourModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Reset to first step when tourKey changes (replay tour)
  useEffect(() => {
    setCurrentStep(0);
  }, [tourKey]);
  
  // Select steps based on user role
  const steps = user?.role === 'vendor' ? vendorOnboardingSteps : clientOnboardingSteps;
  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData.icon;
  
  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      onOpenChange(false);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSkip = () => {
    onComplete();
    onOpenChange(false);
  };
  
  const handleCTA = () => {
    if (currentStepData.ctaHref) {
      navigate(currentStepData.ctaHref);
      onComplete();
      onOpenChange(false);
    }
  };

  // Reset to first step when modal opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setCurrentStep(0);
    }
    onOpenChange(newOpen);
  };
  
  return (
    <>
      {/* Render NavigationHighlight outside modal */}
      {open && <NavigationHighlight targetNavHref={currentStepData.navHref} />}
      
      <Dialog open={open} onOpenChange={handleOpenChange} modal>
        {/* Custom DialogContent without the dark overlay */}
        <DialogPrimitive.Portal>
          {/* Transparent overlay - we use our own NavigationHighlight for dimming */}
          <DialogPrimitive.Overlay className="fixed inset-0" style={{ zIndex: 9996, backgroundColor: 'transparent' }} />
          
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-[9999] grid w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            )}
            onInteractOutside={(e) => e.preventDefault()}
          >
          <DialogHeader>
            <div className="flex items-center justify-between mb-2">
              <DialogTitle className="text-sm text-muted-foreground font-normal">
                Step {currentStep + 1} of {totalSteps}
              </DialogTitle>
            </div>
            <Progress value={((currentStep + 1) / totalSteps) * 100} className="mb-4" />
          </DialogHeader>
          
          {/* Step content */}
          <div className="py-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <StepIcon className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle className="text-2xl font-bold">
                {currentStepData.title}
              </DialogTitle>
            </div>
            
            <DialogDescription className="text-base mb-6">
              {currentStepData.description}
            </DialogDescription>
            
            <div className="space-y-3">
              <h3 className="font-semibold text-foreground">Quick Tips:</h3>
              <ul className="space-y-2">
                {currentStepData.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Optional CTA button */}
            {currentStepData.ctaHref && (
              <Button 
                className="mt-6" 
                variant="outline"
                onClick={handleCTA}
              >
                {currentStepData.ctaText}
              </Button>
            )}
          </div>
          
          {/* Navigation buttons */}
          <DialogFooter className="flex flex-row justify-between items-center sm:justify-between">
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              Skip Tour
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleBack}
                disabled={isFirstStep}
              >
                Back
              </Button>
              <Button onClick={handleNext}>
                {isLastStep ? 'Finish' : 'Next'}
              </Button>
            </div>
          </DialogFooter>
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </Dialog>
    </>
  );
}
